/**
 * Icebreaker questions, written for CitationEasy for classrooms and meetings.
 *
 * Each one is open (no yes/no answer), safe to answer in front of a group, and free of
 * politics, religion, money, health, family structure, dating and anything else a person
 * might not want to disclose to a room. The test suite holds the list to that.
 *
 * ## The row format
 *
 * `depth|setting|question`, where depth is 1 (light), 2 (medium) or 3 (deeper), and
 * setting is c (suits a classroom), w (suits a meeting or workplace) or b (suits both).
 * One string per line keeps the list easy to edit and review.
 */

import {
  randomBelow,
  sample,
  type Uint32Source,
} from "@/lib/tool-kit/random-fun/rng";

export type Depth = 1 | 2 | 3;
export type Setting = "classroom" | "workplace" | "anywhere";

export interface Icebreaker {
  text: string;
  depth: Depth;
  /** Which settings it suits: "both" appears under every setting. */
  suits: "classroom" | "workplace" | "both";
}

const ROWS: readonly string[] = [
  /* Light: a few words each, safe for any size of group. */
  "1|b|What is your favorite way to spend a rainy day?",
  "1|b|If you could instantly learn any skill, what would it be?",
  "1|b|What food could you eat every week without getting bored of it?",
  "1|b|Which season do you like best, and why?",
  "1|b|What is the best thing you have watched, read or listened to lately?",
  "1|b|Would you rather live by the sea or in the mountains, and why?",
  "1|b|What is a small thing that always makes your day better?",
  "1|b|What is the best place you have ever visited?",
  "1|b|If you could have any animal as a pet, which would you choose?",
  "1|b|What one word would your friends use to describe you?",
  "1|b|Are you a morning person or a night owl, and what does that look like for you?",
  "1|b|What hobby would you like to try someday?",
  "1|b|What is your go-to snack?",
  "1|b|If you could travel anywhere tomorrow, where would you go?",
  "1|b|What is the most useful thing you own?",
  "1|b|Which superpower would you choose, and what would you do with it first?",
  "1|b|What song could you listen to on repeat?",
  "1|b|What is your favorite thing to do on a free weekend?",
  "1|b|Which holiday or celebration do you enjoy most?",
  "1|b|If you could have dinner with anyone from history, who would it be?",
  "1|c|What is your favorite subject at school, and what makes it interesting?",
  "1|c|What is the best book you have read this year?",
  "1|c|If you could add one class to the timetable, what would it be?",
  "1|c|What is something you are good at that most people here might not know?",
  "1|c|What is your favorite way to study or learn something new?",
  "1|c|If you were the teacher for a day, what would you teach?",
  "1|c|What is your favorite game or sport to play or watch?",
  "1|c|What would you name a new school club, and what would it do?",
  "1|c|What is the most interesting thing you have learned this week?",
  "1|w|What is your favorite part of a typical workday?",
  "1|w|What is the best piece of advice you have received about work?",
  "1|w|What is one tool or app you could not work without?",
  "1|w|How do you start your morning before work?",
  "1|w|What is one small win from the past week?",
  "1|w|If you could swap jobs with someone for a day, whose job would you pick?",
  "1|w|What was your first job, and what did it teach you?",
  "1|w|What does your ideal lunch break look like?",
  "1|w|What is the most useful shortcut or trick you know?",
  "1|w|What would you do with an extra hour in your workday?",
  "1|w|What are you looking forward to this month?",
  /* Medium: a short story or a reason, about a minute each. */
  "2|b|What is something you have changed your mind about in the past few years?",
  "2|b|Describe a time you learned something from a mistake.",
  "2|b|What is a skill you have that took you a long time to learn?",
  "2|b|Who has been a good teacher or mentor to you, and what did they do well?",
  "2|b|What is a goal you are working toward right now?",
  "2|b|What does a great day look like for you, from morning to night?",
  "2|b|What is something you are curious about that you rarely get to talk about?",
  "2|b|What is a tradition from your family or community that you value?",
  "2|b|What is a challenge you have overcome that you are proud of?",
  "2|b|If you could give your younger self one piece of advice, what would it be?",
  "2|b|What do you do to recharge when you are tired or stressed?",
  "2|b|What is the best compliment you have ever received?",
  "2|b|What would you like others to know about how you work best?",
  "2|b|What is a risk you took that paid off or taught you something?",
  "2|b|What is an invention you wish existed?",
  "2|b|What is something you do differently from most people you know?",
  "2|b|What kind of environment helps you focus best?",
  "2|b|Which person from history would you most like to interview, and what would you ask?",
  "2|b|What is a place that feels like home to you, and why?",
  "2|c|What helps you learn best: reading, listening, doing or discussing?",
  "2|c|What is the hardest thing you have had to learn, and what helped?",
  "2|c|What is a question about the world you would like this class to answer together?",
  "2|c|What would make this class a place where you do your best work?",
  "2|c|What does being a good classmate mean to you?",
  "2|c|What is a subject you used to dislike but now find interesting?",
  "2|c|What is something you are proud of from this year?",
  "2|c|What do you wish teachers knew about how students learn?",
  "2|w|What makes a meeting useful to you?",
  "2|w|Describe the best team you have been part of. What made it work?",
  "2|w|What is a project you are proud of, and what was your part in it?",
  "2|w|What kind of feedback helps you most, and how do you like to receive it?",
  "2|w|What do you wish you had known when you started in your field?",
  "2|w|What is one thing our team could stop doing to save time?",
  "2|w|What is a skill you would like to build this year?",
  "2|w|What does good communication look like on a team?",
  "2|w|How do you prefer to be recognized when you do good work?",
  "2|w|What is a work habit you rely on?",
  /* Deeper: reflective, two to three minutes each; best in pairs or small groups. */
  "3|b|What value matters most to you, and how does it show up in your choices?",
  "3|b|What is a moment that changed how you see things?",
  "3|b|What would you try if you knew you could not fail?",
  "3|b|What are you most grateful for right now, and who helped make it possible?",
  "3|b|What does success mean to you beyond grades or job titles?",
  "3|b|Describe a time when someone's kindness made a real difference to you.",
  "3|b|What is something you are still figuring out about yourself?",
  "3|b|What is a fear you have faced or are working on, and what helps?",
  "3|b|What kind of person do you want to be known as in ten years?",
  "3|b|What is a lesson you learned the hard way?",
  "3|b|What is something you wish you did more of?",
  "3|b|When do you feel most like yourself?",
  "3|b|What is a belief about yourself that you have outgrown?",
  "3|b|What does it mean to you to be a good friend or colleague?",
  "3|c|What do you want to be different about how you learn by the end of this term?",
  "3|c|Describe a time you helped someone understand something. How did it feel?",
  "3|c|What is the biggest thing you hope to get out of this course?",
  "3|c|What makes it hard to ask for help, and what makes it easier?",
  "3|c|What kind of learner are you becoming?",
  "3|w|Describe a difficult conversation you handled well. What did you learn?",
  "3|w|When have you felt most trusted at work, and what created that trust?",
  "3|w|What energizes you in your work, and what drains you?",
  "3|w|What is a risk you would like our team to be willing to take?",
  "3|w|What is the most important thing a leader has done for you?",
  "3|w|What would make you feel you belong here?",
  "3|w|What is a mistake at work that taught you the most?",
  "3|w|What do you hope to be doing in your work five years from now?",
  "3|w|What support would help you do your best work in the coming months?",
];

const SUITS: Record<string, Icebreaker["suits"]> = {
  b: "both",
  c: "classroom",
  w: "workplace",
};

function parseRow(row: string): Icebreaker {
  const [depth = "", setting = "", ...rest] = row.split("|");
  return {
    depth: Number(depth) as Depth,
    suits: SUITS[setting] ?? "both",
    text: rest.join("|"),
  };
}

export const ICEBREAKERS: readonly Icebreaker[] = ROWS.map(parseRow);

/** Seconds one person needs to answer a question of each depth, as a planning estimate. */
export const SECONDS_PER_ANSWER: Record<Depth, number> = {
  1: 20,
  2: 45,
  3: 90,
};

/** A round of icebreakers should fit in about ten minutes, however many people there are. */
export const ROUND_BUDGET_SECONDS = 600;

export interface SessionPlan {
  /** Whole-group time for everyone to answer once, in minutes (rounded up). */
  minutes: number;
  /** True when one round would overrun the budget and smaller groups are better. */
  split: boolean;
  /** How many groups to split into (1 when no split is needed). */
  groups: number;
  /** People per group, rounded up. */
  groupSize: number;
  /** Minutes for one round inside each of those groups (rounded up). */
  groupMinutes: number;
}

/**
 * How long a question takes round a group, and when to split into smaller groups.
 *
 * Time is `people × seconds per answer`. If that is more than ten minutes, the plan
 * splits the group so each smaller group fits the budget; the size is the largest that
 * does.
 */
export function planSession(people: number, depth: Depth): SessionPlan {
  const count = Math.max(1, Math.trunc(people));
  const seconds = SECONDS_PER_ANSWER[depth];
  const minutes = Math.ceil((count * seconds) / 60);
  const fits = Math.max(1, Math.floor(ROUND_BUDGET_SECONDS / seconds));
  const split = count > fits;
  const groups = split ? Math.ceil(count / fits) : 1;
  const groupSize = Math.ceil(count / groups);
  return {
    minutes,
    split,
    groups,
    groupSize,
    groupMinutes: Math.ceil((groupSize * seconds) / 60),
  };
}

/** The questions that suit a setting and (optionally) one depth. */
export function questionPool(
  setting: Setting,
  depth: Depth | "any"
): Icebreaker[] {
  return ICEBREAKERS.filter((question) => {
    const settingOk =
      setting === "anywhere"
        ? question.suits === "both"
        : question.suits === "both" ||
          question.suits ===
            (setting === "classroom" ? "classroom" : "workplace");
    return settingOk && (depth === "any" || question.depth === depth);
  });
}

/** `count` different questions from the pool, in random order. */
export function drawQuestions(
  pool: readonly Icebreaker[],
  count: number,
  source?: Uint32Source
): Icebreaker[] {
  return sample(pool, count, source);
}

/** A different question from the pool than any in `shown`, or null if none is left. */
export function swapQuestion(
  pool: readonly Icebreaker[],
  shown: readonly Icebreaker[],
  source?: Uint32Source
): Icebreaker | null {
  const spare = pool.filter((question) => !shown.includes(question));
  if (spare.length === 0) {
    return null;
  }
  return spare[randomBelow(spare.length, source)] ?? null;
}
